import { useState } from 'react';
import { FESTIVITIES_START } from './Countdown';
import { useSchedule, type WeddingEvent } from '../lib/content';
import {
  clearPinnedClock, fmtDay, fmtTime, isClockPinned, parseKhaoLak, pinClock, pinnedClock,
  toKhaoLakLocal,
} from '../lib/time';

/**
 * Move the clock, so the week can be looked at before it happens.
 *
 * `?at=2026-11-06T19:45` has always done this (`lib/time`). What it did not do
 * is tell you which moments are worth asking for: the interesting ones are the
 * boundaries — the second the countdown turns over and gives its space back,
 * the half hour when an event is "Up next", the minute it becomes "Right now",
 * the gap where "Later today" empties out — and finding those by hand means
 * reading `schedule.json` and doing arithmetic in your head. **So the presets
 * are derived from the schedule rather than written down**, which is the only
 * version of this that cannot go stale: move the sangeet in the JSON (D12, no
 * deploy) and the moments move with it.
 *
 * **Dev builds only**, like `BackendBadge` — `import.meta.env.DEV` is a literal
 * `false` in a real build, so the guard returns before any of this exists and
 * the whole component is eliminated. A pinned clock is still reachable in
 * production by way of `?at=`, and `Profile` still carries the way out of it;
 * what does not ship is the furniture. A guest forwarded a link with `?at=` on
 * it should see a wrong date, which is confusing, rather than a control panel,
 * which is alarming.
 *
 * Bottom-left above the backend badge, which owns the corner itself. Collapsed
 * by default and a `<details>` rather than a `useState` toggle, because the
 * open/close behaviour, the keyboard handling and the `aria-expanded` are all
 * already in the element.
 */
export default function ClockBar() {
  if (!import.meta.env.DEV) return null;
  // The hooks live one level down so the guard above can be an early return
  // without ever being a conditional hook.
  return <ClockPicker />;
}

export interface Moment { label: string; at: number }
export interface MomentGroup { label: string; moments: Moment[] }

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/**
 * The moments worth jumping to, grouped.
 *
 * Exported for its test. It takes events rather than reading them so it is a
 * pure function of the schedule, and it sorts rather than trusting the file —
 * `schedule.json` is hand-edited from a phone and nothing enforces its order.
 *
 * Each event contributes two: half an hour out, when it is the thing "Up next"
 * is about, and the middle of it, when it is "Right now" and everything else
 * that day has moved to "Later today". The midpoint rather than the start
 * because a start time lands exactly on the boundary between the two states,
 * which is the one place a rounding error would hide.
 */
export function momentsOf(events: WeddingEvent[]): MomentGroup[] {
  const groups: MomentGroup[] = [{
    label: 'The wait',
    moments: [
      { label: 'A week out', at: FESTIVITIES_START - 7 * DAY },
      { label: 'The night before', at: FESTIVITIES_START - 6 * HOUR },
      { label: 'Five seconds out', at: FESTIVITIES_START - 5_000 },
      { label: 'The moment it turns', at: FESTIVITIES_START },
    ],
  }];

  const sorted = [...events].sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
  if (sorted.length === 0) return groups;

  const moments: Moment[] = [];
  for (const event of sorted) {
    const start = Date.parse(event.start);
    const end = event.end ? Date.parse(event.end) : start + HOUR;
    moments.push({ label: `${event.name} · half an hour out`, at: start - 30 * MIN });
    moments.push({ label: `${event.name} · under way`, at: Math.round((start + end) / 2) });
  }
  groups.push({ label: 'The week', moments });

  const last = Math.max(...sorted.map((e) => Date.parse(e.end ?? e.start)));
  groups.push({
    label: 'After',
    moments: [{ label: "Once it's all over", at: last + HOUR }],
  });
  return groups;
}

function ClockPicker() {
  const schedule = useSchedule();
  const pinned = pinnedClock();
  // Seeded from the pin so the field agrees with the bar above it, and so
  // nudging a moment by ten minutes is an edit rather than a re-type.
  const [typed, setTyped] = useState(() => toKhaoLakLocal(pinned ?? Date.now()));
  const [bad, setBad] = useState(false);

  const groups = momentsOf(schedule.data?.events ?? []);

  // Every one of these ends in a reload. `now()` is sampled during render and
  // nothing subscribes to it, so a pin without a reload would change the answer
  // for whatever mounts next and leave the current screen quietly stale — which
  // reads as the pin not having worked.
  const pinTo = (ms: number) => { pinClock(ms); window.location.reload(); };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const ms = parseKhaoLak(typed);
    if (ms === null) { setBad(true); return; }
    pinTo(ms);
  };

  return (
    <details className="clock-bar">
      <summary>
        <span aria-hidden="true">◷</span>
        {isClockPinned() && pinned !== null
          ? <span className="clock-bar-at">{fmtDay(pinned)}, {fmtTime(pinned)}</span>
          : <span className="clock-bar-at">Clock</span>}
      </summary>

      <div className="clock-bar-panel">
        <p className="clock-bar-note">
          Pins the clock for this tab. All times Khao Lak.
        </p>

        <select
          aria-label="Jump to a moment"
          value=""
          onChange={(e) => { if (e.target.value) pinTo(Number(e.target.value)); }}
        >
          <option value="">Jump to a moment…</option>
          {groups.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.moments.map((moment) => (
                <option key={`${group.label}:${moment.label}`} value={String(moment.at)}>
                  {moment.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>

        <form onSubmit={submit}>
          {/* `datetime-local` rather than a text box: the browser does the
              validating, the value can only ever arrive in one shape, and on a
              phone it opens the native picker instead of a keyboard. */}
          <input
            type="datetime-local"
            aria-label="Pin the clock to (Khao Lak time)"
            aria-invalid={bad || undefined}
            value={typed}
            onChange={(e) => { setTyped(e.target.value); setBad(false); }}
          />
          <button type="submit">Pin</button>
        </form>
        {bad && <p className="clock-bar-bad" role="alert">Not a date I can read.</p>}

        {isClockPinned() && (
          <button
            type="button" className="clock-bar-clear"
            onClick={() => { clearPinnedClock(); window.location.reload(); }}
          >
            Now, really
          </button>
        )}
      </div>
    </details>
  );
}
